/**
 * Labels aligned with CombinedRuleBuilder / Rules Management so alert copy matches rule building.
 */

const OFFENCE = [
  { code: 'TF', desc: 'Financing of terrorism' },
  { code: 'ML', desc: 'Money laundering' },
  { code: 'OG', desc: 'Offence against Commonwealth/State/Territory law' },
  { code: 'FI', desc: 'Person/agent not who they claim to be' },
  { code: 'PC', desc: 'Proceeds of crime' },
  { code: 'TE', desc: 'Tax evasion' },
]
const SERVICE = [
  { code: 'FIN_EFT', desc: 'Electronic funds transfers (EFT)' },
  { code: 'GAMCHSKL', desc: 'Games of chance or skill' },
  { code: 'GAM_BETT', desc: 'Gambling and betting services' },
  { code: 'GAM_MACH', desc: 'Gaming machines' },
  { code: 'VALCARDS', desc: 'Stored value cards' },
]
const REASON = [
  { code: 'AV', desc: 'Avoiding reporting obligations (structuring)' },
  { code: 'UA', desc: 'Unusual account activity' },
  { code: 'UG', desc: 'Unusual gambling activity' },
  { code: 'UC', desc: 'Unusually large cash transaction' },
  { code: 'UT', desc: 'Unusually large transfer' },
  { code: 'IC', desc: 'Inconsistent with customer profile' },
]

function mapLookup(list, code) {
  const c = String(code || '').trim()
  const row = list.find((x) => x.code === c)
  return row ? `${row.code} — ${row.desc}` : c || '—'
}

/** Rule builder "Risk Assessment" tiers (low | medium | high | critical). */
export function formatRiskTierLabel(riskLevel) {
  const k = String(riskLevel || '').toLowerCase()
  const map = { low: 'Low', medium: 'Medium', high: 'High', critical: 'Critical' }
  return map[k] || (riskLevel ? String(riskLevel) : '—')
}

/** Rule builder "Severity" dropdown (LOW … CRITICAL) + alert row severity. */
export function formatSeverityLabel(severity) {
  if (severity == null || severity === '') return '—'
  const s = String(severity).trim().toUpperCase()
  const map = { LOW: 'LOW', MEDIUM: 'MEDIUM', HIGH: 'HIGH', CRITICAL: 'CRITICAL' }
  return map[s] || s
}

/**
 * Same values as Processing Category in CombinedRuleBuilder:
 * session-based | cumulative
 */
export function formatProcessingCategoryLabel(processingCategory) {
  const k = String(processingCategory || '').toLowerCase()
  if (k === 'cumulative') return 'Cumulative'
  if (k === 'session-based' || k === 'session_based') return 'Session-based'
  return processingCategory ? String(processingCategory) : '—'
}

export function processingCategoryExplanation(processingCategory) {
  const k = String(processingCategory || '').toLowerCase()
  if (k === 'cumulative') {
    return 'Rolled-up over a time window (same as Processing Category: Cumulative in the rule builder).'
  }
  if (k === 'session-based' || k === 'session_based') {
    return 'Evaluated on each feed row / session (same as Processing Category: Session-based).'
  }
  return null
}

export function formatRuleTypeUi(ruleType) {
  if (!ruleType) return '—'
  const t = String(ruleType).toLowerCase()
  if (t === 'transaction') return 'Membership / transactions'
  if (t === 'session') return 'Gaming'
  if (t === 'payout') return 'Payout'
  return String(ruleType)
}

export function formatTagLines(tagsData) {
  const d = tagsData || {}
  const lines = []
  for (const c of d.offence_codes || []) lines.push({ group: 'Offence type (SMR)', line: mapLookup(OFFENCE, c) })
  for (const c of d.service_codes || []) lines.push({ group: 'Designated service', line: mapLookup(SERVICE, c) })
  for (const c of d.reason_codes || []) lines.push({ group: 'Suspicion reason', line: mapLookup(REASON, c) })
  return lines
}

/** True when the rule has AUSTRAC SMR metadata tags configured in the rule builder (offence / service / reason codes). */
export function ruleHasAustracSmrTags(tagsData) {
  const d = tagsData || {}
  const n = (a) => (Array.isArray(a) ? a.filter(Boolean).length : 0)
  return n(d.offence_codes) + n(d.service_codes) + n(d.reason_codes) > 0
}

/** Tooltip text for the AUSTRAC chip — same lines as the Rule tags list. */
export function buildAustracTagTooltip(tagsData) {
  const lines = formatTagLines(tagsData)
  if (!lines.length) return ''
  return lines.map((x) => `${x.group}: ${x.line}`).join('\n')
}

/** Risk tier badge colors — same spirit as CombinedRuleBuilder Risk Assessment cards. */
export function riskTierStyle(tier) {
  const k = String(tier || '').toLowerCase()
  if (k === 'low') return { bg: '#ecfdf5', border: '#86efac', color: '#166534' }
  if (k === 'medium') return { bg: '#fffbeb', border: '#fde047', color: '#a16207' }
  if (k === 'high') return { bg: '#ffedd5', border: '#fdba74', color: '#c2410c' }
  if (k === 'critical') return { bg: '#fee2e2', border: '#fca5a5', color: '#991b1b' }
  return { bg: '#f1f5f9', border: '#e2e8f0', color: '#475569' }
}
